import { useState, useRef } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Check, Phone, Plus, Minus, Building2 } from "lucide-react";
import { Eyebrow } from "@/components/ui-custom/Eyebrow";
import { Reveal } from "@/components/ui-custom/Reveal";
import { BeforeAfterSlider } from "@/components/ui-custom/BeforeAfterSlider";
import { GalleryStrip } from "@/components/ui-custom/GalleryStrip";
import { Lightbox } from "@/components/ui-custom/Lightbox";
import { QuoteForm } from "@/components/ui-custom/QuoteForm";
import { PreFooterCTA } from "@/components/sections/PreFooterCTA";
import SERVICES from "@/data/services";
import { SERVICE_MEDIA } from "@/data/media";
import { PROCESS, EXTRA_FAQS } from "@/data/serviceExtras";
import { SITE } from "@/data/site";
import { useSEO, serviceSchema, faqSchema } from "@/lib/seo";

const TOWNS = ["Colts Neck", "Holmdel", "Marlboro", "Freehold", "Middletown", "Manalapan", "Rumson", "Red Bank", "Wall", "Howell"];

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div style={{ borderBottom: "1px solid #d9d4c8" }} data-testid={`faq-item-${index}`}>
      <button className="w-full flex items-center justify-between gap-4 text-left py-5" onClick={() => setOpen((o) => !o)} data-testid={`faq-toggle-${index}`}>
        <span className="h3" style={{ fontSize: "19px", color: "var(--text-dark)" }}>{q}</span>
        <span style={{ color: "var(--cta)", flexShrink: 0 }}>{open ? <Minus size={22} strokeWidth={1.75} /> : <Plus size={22} strokeWidth={1.75} />}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
        <p className="pb-6" style={{ color: "var(--text-muted)", fontSize: "16px", lineHeight: 1.7, maxWidth: "70ch" }}>{a}</p>
      </motion.div>
    </div>
  );
};

const HowItWorks = ({ steps }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.min(steps.length - 1, Math.max(0, Math.floor(v * steps.length)))));
  const s = steps[active];

  return (
    <section ref={ref} className="sec-black" style={{ height: `${steps.length * 78}vh` }} data-testid="service-process">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="container-1200 grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16 w-full">
          <div>
            <Eyebrow start>How It Works</Eyebrow>
            <h2 className="h2 mt-5" style={{ color: "var(--text-light)", maxWidth: "14ch" }}>No Surprises, Start to Finish</h2>
            <div className="flex flex-col gap-2 mt-9">
              {steps.map((st, i) => (
                <button
                  key={i}
                  onClick={() => window.__lenis && window.__lenis.scrollTo(ref.current.offsetTop + (i + 0.5) * (ref.current.offsetHeight / steps.length))}
                  className="flex items-center gap-3 text-left py-1.5"
                  style={{ opacity: i === active ? 1 : 0.4, transition: "opacity 0.3s ease" }}
                  data-testid={`process-nav-${i}`}
                >
                  <span style={{ width: 26, height: 26, borderRadius: 999, border: `1.5px solid ${i === active ? "var(--cta)" : "var(--border-dark)"}`, color: i === active ? "var(--cta)" : "rgba(245,242,236,0.6)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, fontFamily: "Inter" }}>{i + 1}</span>
                  <span style={{ color: "var(--text-light)", fontSize: 15, fontWeight: 500 }}>{st.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="relative" style={{ minHeight: 300 }}>
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 48 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -36 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} data-testid={`process-step-${active}`}>
                <span className="gold-numeral" style={{ fontSize: "clamp(72px, 12vw, 140px)", lineHeight: 0.9 }}>{String(active + 1).padStart(2, "0")}</span>
                <h3 className="font-head uppercase mt-4" style={{ color: "var(--text-light)", fontSize: "clamp(26px,3.6vw,42px)", letterSpacing: "0.015em", lineHeight: 1.05 }}>{s.title}</h3>
                <p className="mt-5" style={{ color: "rgba(245,242,236,0.72)", fontSize: 18, lineHeight: 1.6, maxWidth: "42ch" }}>{s.desc}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES[slug];
  const [lightbox, setLightbox] = useState(null);

  const faqs = service ? [...service.faqs, ...(EXTRA_FAQS[slug] || [])] : [];

  useSEO({
    title: service?.metaTitle,
    description: service?.metaDescription,
    path: service?.path || "/",
    jsonLd: service ? [serviceSchema(service), faqSchema(faqs)] : [],
  });

  if (!service) return <Navigate to="/" replace />;

  const steps = PROCESS[slug] || [];
  const ba = service.beforeAfter[0];
  const media = SERVICE_MEDIA[slug] || {};
  const hasBA = Boolean(media.before && media.after);
  const gallery = (media.gallery || []).map((src, i) => ({
    src,
    alt: `${service.cardTitle} project in ${TOWNS[i % TOWNS.length]}, NJ`,
    label: `${service.cardTitle} ${i + 1}`,
  }));

  return (
    <>
      {/* Hero — 3/4 screen */}
      <section className="relative overflow-hidden" style={{ minHeight: "75vh", background: "var(--black)", marginTop: "-72px" }} data-testid="service-hero">
        <div className="absolute inset-0" style={{ background: "var(--charcoal)" }} />
        {media.hero ? (
          <img src={media.hero} alt={service.h1} className="absolute inset-0" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : null}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(9,9,11,0.84) 0%, rgba(9,9,11,0.5) 48%, rgba(9,9,11,0.18) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(9,9,11,0.5) 0%, rgba(9,9,11,0) 40%)" }} />
        <div className="container-1200 relative z-10 flex flex-col justify-center" style={{ minHeight: "75vh", paddingTop: "112px", paddingBottom: "64px" }}>
          <Reveal><Eyebrow start data-testid="service-eyebrow">{service.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}><h1 className="h1 mt-6" style={{ color: "var(--text-light)", maxWidth: "20ch" }} data-testid="service-h1">{service.h1}</h1></Reveal>
          <Reveal delay={0.2}><p className="lead mt-6" style={{ color: "rgba(245,242,236,0.82)", maxWidth: "52ch" }} data-testid="service-subline">{service.heroSubline}</p></Reveal>
          <Reveal delay={0.3}><Link to="/contact#quote" className="btn btn-primary mt-9 self-start" data-testid="service-hero-cta">Get My Free Estimate</Link></Reveal>
        </div>
      </section>

      {/* Intro + sidebar */}
      <section className="sec-offwhite section-y" data-testid="service-intro">
        <div className="container-1200 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <Reveal>
              <div className="eyebrow eyebrow-start">Sound Familiar?</div>
              <ul className="mt-5 space-y-3">
                {service.soundFamiliar.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`sound-familiar-${i}`}>
                    <span className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(245,207,112,0.18)", marginTop: 1 }}>
                      <Check size={16} strokeWidth={2} style={{ color: "var(--cta-deep)" }} />
                    </span>
                    <span style={{ fontSize: "17px", color: "var(--text-dark)", lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="h3 mt-10" style={{ fontSize: "26px", color: "var(--text-dark)", textTransform: "none", letterSpacing: 0 }}>{service.outcomeHeadline}</h2>
              <div className="mt-5 space-y-5" style={{ fontSize: "17px", lineHeight: 1.75, color: "var(--text-dark)" }}>
                {service.intro.map((p, i) => (<p key={i}>{p}</p>))}
              </div>
            </Reveal>

            {service.nyc && (
              <Reveal delay={0.1}>
                <div className="mt-10 p-8" style={{ background: "var(--charcoal)", borderRadius: 8, border: "1px solid var(--border-dark)" }} data-testid="nyc-block">
                  <div className="eyebrow eyebrow-start">NYC Property Maintenance</div>
                  <h3 className="h3 mt-4" style={{ color: "var(--text-light)" }}>Registered for New York City Property Maintenance</h3>
                  <p className="mt-4" style={{ color: "rgba(245,242,236,0.72)", fontSize: "16px", lineHeight: 1.7 }}>Managing a property across the river? You're covered there too. We're registered with New York City for NY housing property maintenance, so the same reliable crew can support your facilities in the city as well as across Central New Jersey.</p>
                  <div className="flex flex-wrap gap-3 mt-6" data-testid="client-types">
                    {service.clientTypes.map((c) => (
                      <span key={c} className="inline-flex items-center gap-2" style={{ border: "1px solid var(--cta)", color: "var(--cta)", borderRadius: 6, padding: "8px 14px", fontSize: "14px", fontWeight: 500 }}>
                        <Building2 size={15} strokeWidth={1.5} />{c}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>

          <div id="quote" className="lg:sticky lg:top-[104px]">
            <Reveal delay={0.15}>
              <div style={{ background: "var(--black)", borderTop: "3px solid var(--cta)", borderRadius: 8, padding: "28px" }} data-testid="service-sidebar">
                <h3 className="h3" style={{ color: "var(--text-light)" }}>Get a Free Estimate</h3>
                <ul className="mt-5 space-y-2.5">
                  {["Licensed & Insured", `NJ HIC ${SITE.hicNumber}`, "20+ Years Experience", "Free Estimates — No Pressure"].map((t) => (
                    <li key={t} className="flex items-center gap-2.5" style={{ color: "rgba(245,242,236,0.8)", fontSize: "14px" }}>
                      <Check size={16} strokeWidth={2} style={{ color: "var(--cta)" }} />{t}
                    </li>
                  ))}
                </ul>
                <a href={SITE.phoneHref} className="flex items-center gap-2.5 mt-5 mb-6" style={{ color: "var(--text-light)", fontWeight: 600, fontSize: "18px" }} data-testid="sidebar-phone">
                  <Phone size={18} strokeWidth={1.75} style={{ color: "var(--cta)" }} />{SITE.phone}
                </a>
                <div style={{ borderTop: "1px solid var(--border-dark)", paddingTop: "20px" }}>
                  <QuoteForm variant="mini" preselectService={service.cardTitle} source={`service_${service.slug}`} />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How it works — pinned timeline */}
      <HowItWorks steps={steps} />

      {/* Before & After / Featured result */}
      <section className="sec-white section-y" data-testid="service-before-after">
        <div className="container-1200">
          <Reveal>
            <Eyebrow start>Real Results</Eyebrow>
            <h2 className="h2 mt-5">{hasBA ? `${service.cardTitle} — Before & After` : `A Finished ${service.cardTitle} Project`}</h2>
            <p className="mt-4 text-muted-warm" style={{ fontSize: "16px", maxWidth: "60ch" }}>
              {hasBA ? "Drag the slider to see one of our real Monmouth County transformations." : "A recent AIM High job — real photos from a completed project."}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 mx-auto" style={{ maxWidth: 900 }}>
              {hasBA ? (
                <BeforeAfterSlider
                  caption={ba.caption}
                  beforeSrc={media.before}
                  afterSrc={media.after}
                  beforeAlt={`${service.cardTitle} before`}
                  afterAlt={`${service.cardTitle} after`}
                  data-testid="before-after-0"
                />
              ) : (
                <div className="overflow-hidden" style={{ borderRadius: 10, boxShadow: "0 28px 60px rgba(23,23,26,0.16)" }} data-testid="featured-result">
                  <img
                    src={media.showcase || media.hero}
                    alt={`Completed ${service.cardTitle} project by AIM High`}
                    style={{ width: "100%", display: "block", aspectRatio: "16 / 10", objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Gallery — clean auto-scrolling strip */}
      <section className="sec-gray section-y" data-testid="service-gallery">
        <div className="container-1200">
          <Reveal>
            <Eyebrow start>Our Work</Eyebrow>
            <h2 className="h2 mt-5">More {service.cardTitle} Work</h2>
            <p className="mt-4 text-muted-warm" style={{ fontSize: 15 }}>Hover to pause · click any photo to view it full-size.</p>
          </Reveal>
        </div>
        <div className="mt-12">
          <GalleryStrip items={gallery} onSelect={(i) => setLightbox(i)} />
        </div>
      </section>

      {/* FAQ — 6 */}
      <section className="sec-offwhite section-y" data-testid="service-faq">
        <div className="container-1200 grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16">
          <Reveal>
            <Eyebrow start>Good Questions</Eyebrow>
            <h2 className="h2 mt-5" style={{ maxWidth: "14ch" }}>Answers Before You Ask</h2>
            <p className="mt-5 text-muted-warm" style={{ fontSize: "16px" }}>The things people actually want to know — answered straight.</p>
          </Reveal>
          <div>
            {faqs.map((f, i) => (<FaqItem key={i} q={f.q} a={f.a} index={i} />))}
          </div>
        </div>
      </section>

      <PreFooterCTA />

      {lightbox !== null && (
        <Lightbox
          items={gallery}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => (i - 1 + gallery.length) % gallery.length)}
          onNext={() => setLightbox((i) => (i + 1) % gallery.length)}
        />
      )}
    </>
  );
}
